import { ScrollReveal, SplitReveal } from '@/components/motion';
import {
  CONTACT_ADDRESS,
  CONTACT_CAMPUS_HOURS,
  CONTACT_DEPARTMENTS,
  CONTACT_EMAILS,
  CONTACT_ICONS,
  CONTACT_PHONE,
  CONTACT_PHONE_HOURS,
  CONTACT_SOCIAL,
} from '@/data/about';

/** Shared underline-on-hover idiom for navy contact links (email / phone / tel). */
const CONTACT_LINK =
  'font-medium text-brand-navy underline decoration-2 decoration-transparent underline-offset-4 transition-colors duration-300 hover:decoration-brand-gold';

/** `tel:` sanitiser for a phone number typed with spaces. */
const telHref = (phone: string) => `tel:${phone.replace(/[^+\d]/g, '')}`;

/**
 * ContactUs (/about — section 9) — "Get in touch."
 *
 * The mandated contact directory (Appendix B verbatim departmental table +
 * /contact-us/ address, phone and email). A top block pairs the physical
 * address and phone (with its two publish hour strings) against the three
 * CONTACT_EMAILS as mailto cards. Below, every CONTACT_DEPARTMENTS entry
 * renders as a definition card — role, contact (mailto: when it is an email,
 * plain text otherwise), verbatim support areas and availability. A closing
 * social row links out to the four verified CONTACT_SOCIAL profiles.
 *
 * NOTE: the full "send us a message" form lives in the shared EnquiryForm
 * rendered immediately after this section on the page — this section is the
 * directory only, no duplicate form here.
 *
 * Motion: SplitReveal headline, ScrollReveal child-stagger for the email
 * cards, the department grid and the social row. Reduced-motion safe; every
 * fact is sourced from CONTACT_* in '@/data/about', nothing is invented.
 */
export function ContactUs() {
  const { MapPin, Phone } = CONTACT_ICONS;

  return (
    <section className="border-t border-border bg-muted/30 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading ──────────────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Contact us
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Get in touch.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              Reach the right desk directly, or write to the campus using the details below —
              the team you need is only ever one message away.
            </p>
          </ScrollReveal>
        </header>

        {/* ── Address + phone · email cards ───────────────────────────── */}
        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.2fr] lg:gap-8">
          {/* ── Address & phone ──────────────────────────────────────── */}
          <ScrollReveal from="up" delay={0.1} stagger={0.08} className="flex flex-col gap-6">
            <div className="flex items-start gap-4 rounded-xl border border-border bg-background p-5">
              <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 text-brand-navy">
                <MapPin aria-hidden className="size-5" />
              </div>
              <div className="min-w-0">
                <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                  Address
                </p>
                <p className="mt-1 text-sm leading-relaxed text-foreground">{CONTACT_ADDRESS}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-xl border border-border bg-background p-5">
              <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 text-brand-navy">
                <Phone aria-hidden className="size-5" />
              </div>
              <div className="min-w-0">
                <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                  Phone
                </p>
                <a href={telHref(CONTACT_PHONE)} className={`mt-1 block text-sm ${CONTACT_LINK}`}>
                  {CONTACT_PHONE}
                </a>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  {CONTACT_PHONE_HOURS}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  Campus hours: {CONTACT_CAMPUS_HOURS}
                </p>
              </div>
            </div>
          </ScrollReveal>

          {/* ── Email cards ──────────────────────────────────────────── */}
          <ScrollReveal
            from="up"
            delay={0.15}
            stagger={0.08}
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          >
            {CONTACT_EMAILS.map((entry) => (
              <a
                key={entry.email}
                href={`mailto:${entry.email}`}
                data-cursor
                className="h-full rounded-2xl border border-border bg-background p-6 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <div className="flex size-10 items-center justify-center rounded-full bg-brand-navy/5 text-brand-navy">
                  <entry.Icon aria-hidden className="size-5" />
                </div>
                <p className="mt-4 font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                  {entry.label}
                </p>
                <p className={`mt-1.5 text-sm break-words ${CONTACT_LINK}`}>{entry.email}</p>
              </a>
            ))}
          </ScrollReveal>
        </div>

        {/* ── Departmental contacts directory ─────────────────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Departmental contacts
          </p>
          <ScrollReveal
            stagger={0.08}
            className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2"
          >
            {CONTACT_DEPARTMENTS.map((dept) => {
              const isEmail = dept.contact.includes('@');
              return (
                <div
                  key={dept.role}
                  className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                >
                  <h3 className="font-display text-xl leading-tight font-semibold text-foreground">
                    {dept.role}
                  </h3>
                  {isEmail ? (
                    <a href={`mailto:${dept.contact}`} className={`mt-1.5 block text-sm ${CONTACT_LINK}`}>
                      {dept.contact}
                    </a>
                  ) : (
                    <p className="mt-1.5 text-sm font-medium text-foreground">{dept.contact}</p>
                  )}

                  <ul className="mt-5 space-y-1.5">
                    {dept.supportAreas.map((area) => (
                      <li
                        key={area}
                        className="flex items-start gap-2 text-sm leading-relaxed text-muted-foreground"
                      >
                        <span aria-hidden className="mt-2 size-1 shrink-0 rounded-full bg-brand-gold" />
                        {area}
                      </li>
                    ))}
                  </ul>

                  <p className="mt-5 border-t border-border pt-4 text-xs leading-relaxed text-muted-foreground">
                    {dept.availability}
                  </p>
                </div>
              );
            })}
          </ScrollReveal>
        </div>

        {/* ── Social row ───────────────────────────────────────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Follow IBAS
          </p>
          <ScrollReveal stagger={0.06} className="mt-6 flex flex-wrap gap-4">
            {CONTACT_SOCIAL.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noreferrer"
                data-cursor
                className="flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium text-brand-navy transition-all duration-300 hover:-translate-y-0.5 hover:border-brand-gold/30 hover:shadow-md motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <social.Icon aria-hidden className="size-4" />
                {social.label}
              </a>
            ))}
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
